import { GENDER } from '@shared/constants/user-constants'
import { COUNTRY_CODE } from '@shared/constants/province-constants'

import type { FormData } from './types'

export const OTHER_PREFIX = 'Other:'

export const REASON_LABELS = {
  PERMANENT_RESIDENT: 'Permanent Resident in Canada',
  CANADIAN_CITIZENSHIP: 'Canadian Citizenship',
  EMPLOYMENT: 'Employment',
  VSC: 'VSC (Vulnerable Sector Check) ***',
  PRIVACY_ACT: 'Criminal Record Check for Personal Use (including US Immigration/US Green Card)',
  FOREIGN_COUNTRIES:
    'Foreign Countries - Visa, Travel, Work or Immigration to another country (including US Waiver) ***',
  RECORD_SUSPENSION: 'Record Suspension Application (Pardon) ***',
  NAME_CHANGE: 'Name Change ***',
  ADOPTION: 'Adoption ***',
} as const

export const REASON_OPTIONS = [
  REASON_LABELS.PERMANENT_RESIDENT,
  REASON_LABELS.CANADIAN_CITIZENSHIP,
  REASON_LABELS.EMPLOYMENT,
  REASON_LABELS.PRIVACY_ACT,
  REASON_LABELS.FOREIGN_COUNTRIES,
  REASON_LABELS.RECORD_SUSPENSION,
  REASON_LABELS.NAME_CHANGE,
  REASON_LABELS.ADOPTION,
  REASON_LABELS.VSC,
] as const

export const REASON_TO_APPLICATION_TYPE_CODE: Record<string, string> = {
  [REASON_LABELS.PRIVACY_ACT]: '10',
  [REASON_LABELS.ADOPTION]: '11',
  [REASON_LABELS.PERMANENT_RESIDENT]: '12',
  [REASON_LABELS.RECORD_SUSPENSION]: '23',
  [REASON_LABELS.CANADIAN_CITIZENSHIP]: '26',
  [REASON_LABELS.FOREIGN_COUNTRIES]: '32',
  // Name Change is submitted as "Others" (39) with a default type detail; see getPayloadFromFormData.
  [REASON_LABELS.NAME_CHANGE]: '39',
}

export const NAME_CHANGE_DEFAULT_TYPE_DETAIL = 'Name Change - Alberta'

export const EMPLOYMENT_TYPE_OPTIONS = [
  'Employment (Private Industry) ***',
  'Employment (Provincial Govt) ***',
  'Employment (Federal Govt)',
  'Employment (Police)',
  'Employment (Volunteer)',
  'Employment (Other) ***',
] as const

export const VSC_EMPLOYMENT_TYPE_OPTIONS = [
  'Employment (Private Industry) ***',
  'Employment (Provincial Govt) ***',
  'Employment (Volunteer)',
  'Employment (Other) ***',
] as const

export const EMPLOYMENT_TYPE_TO_CODE: Record<string, string> = {
  'Employment (Private Industry)': '7',
  'Employment (Provincial Govt)': '20',
  'Employment (Federal Govt)': '21',
  'Employment (Other)': '22',
  'Employment (Volunteer)': '25',
  'Employment (Police)': '34',
}

export const REFERRAL_OPTIONS = [
  'Returning Client',
  'Google Search',
  'Google Maps',
  'Friends and family',
  'Private Agencies (e.g., Immigration Consultant, Employment Agency, Employer, etc.)',
  'Government Institutions (e.g., Police, Government Department, etc.)',
  'Facebook, Instagram, Twitter, LinkedIn',
  'Online Forums or Communities',
] as const

export const FORM_STEPS = {
  PERSONAL_INFO: 1,
  SERVICES: 2,
  VSC: 3,
  EMPLOYMENT_TYPE: 4,
  NAME_CHANGE: 5,
  REFERRAL: 6,
  PHOTO_CAPTURE: 7,
  CONSENT: 8,
} as const

export const PHONE_MAX = 20
export const PHONE_MSG = {
  digitsOnly: 'Phone number can only contain digits',
  max: `Phone number cannot exceed ${PHONE_MAX} digits`,
} as const

// Field keys for maintainability
export const FIELD_KEYS = {
  PERSONAL_INFO: {
    CITY: 'city',
    EMAIL: 'email',
    COUNTRY: 'country',
    SURNAME: 'surname',
    ADDRESS: 'address',
    PROVINCE: 'province',
    POSTAL_CODE: 'postalCode',
    GENDER_CODE: 'genderCode',
    GIVEN_NAME_1: 'givenName1',
    GIVEN_NAME_2: 'givenName2',
    GIVEN_NAME_3: 'givenName3',
    GIVEN_NAME_4: 'givenName4',
    PHONE_NUMBER: 'phoneNumber',
    DATE_OF_BIRTH: 'dateOfBirth',
  },

  NAME_CHANGE: {
    NEW_SURNAME: 'newSurname',
    NEW_GIVEN_NAME_1: 'newGivenName1',
    NEW_GIVEN_NAME_2: 'newGivenName2',
    NEW_GIVEN_NAME_3: 'newGivenName3',
    NEW_GIVEN_NAME_4: 'newGivenName4',
  },
} as const

/** Personal info name fields in order (Surname → Given 1–4). Single source of truth for sequential-name logic. */
export const PERSONAL_INFO_NAME_KEYS = [
  FIELD_KEYS.PERSONAL_INFO.SURNAME,
  FIELD_KEYS.PERSONAL_INFO.GIVEN_NAME_1,
  FIELD_KEYS.PERSONAL_INFO.GIVEN_NAME_2,
  FIELD_KEYS.PERSONAL_INFO.GIVEN_NAME_3,
  FIELD_KEYS.PERSONAL_INFO.GIVEN_NAME_4,
] as const

/** Name change name fields in order (New Surname → New Given 1–4). Single source of truth for sequential-name logic. */
export const NAME_CHANGE_NAME_KEYS = [
  FIELD_KEYS.NAME_CHANGE.NEW_SURNAME,
  FIELD_KEYS.NAME_CHANGE.NEW_GIVEN_NAME_1,
  FIELD_KEYS.NAME_CHANGE.NEW_GIVEN_NAME_2,
  FIELD_KEYS.NAME_CHANGE.NEW_GIVEN_NAME_3,
  FIELD_KEYS.NAME_CHANGE.NEW_GIVEN_NAME_4,
] as const

// Regex and limits shared by PersonalInfoStep
export const NAME_REGEX = /^[A-Z\-'. ]*$/
export const INVALID_NAME_CHARS = /[^A-Z\-'. ]+/g
export const NAME_ALLOWED_KEY = /^[A-Za-z\-'. ]+$/

export const ADDRESS_REGEX = /^[A-Z0-9 #$&'()*,+\-./]+$/i
export const ADDRESS_ALLOWED_KEY = /^[A-Za-z0-9 #$&'()*,+\-./]$/

export const DATE_OF_BIRTH_REGEX = /^[0-9/]$/

export const CITY_REGEX = /^[A-Z0-9 #$&'()*,\-.]+$/i
export const CITY_ALLOWED_KEY = /^[A-Za-z0-9 #$&'()*,\-.]$/

export const EMAIL_REGEX = /^(|[^\s@]+@[^\s@]+\.[^\s@]*)$/
export const EMAIL_ALLOWED_KEY = /^[A-Za-z0-9@.\-_]*$/

export const CANADA_POSTAL_REGEX = /^[A-Z]\d[A-Z] \d[A-Z]\d$/i
export const US_POSTAL_REGEX = /^\d{5}(-\d{4})?$/

export const PHONE_NUMBER_REGEX = /^[0-9]*$/
export const INVALID_PHONE_NUMBER_CHARS = /\D/g

export const PASSWORD_REGEX = /^(?=.*[A-Z])(?=.*\d)\S{8,}$/

export const SURNAME_MAX = 24
export const GIVEN_MAX = 15
export const ADDRESS_MAX = 50
export const CITY_MAX = 35
export const DOB_MAX = 10 // YYYY/MM/DD
export const POSTAL_MAX = 10

/** Message when name fields are filled out of order (Surname → Given 1 → 2 → 3 → 4). Used by Name Change and Personal Info steps. */
export const SEQUENTIAL_NAME_ORDER_MESSAGE =
  'Given names must be filled in order (do not skip a field).'

export const MESSAGES = {
  surnameChars: 'Surname can only contain letters, hyphens, apostrophes, periods, and spaces',
  surnameMax: `Surname cannot exceed ${SURNAME_MAX} characters`,
  givenChars: (n: number) =>
    `Given Name ${n} can only contain letters, hyphens, apostrophes, periods and spaces`,
  givenMax: (n: number) => `Given Name ${n} cannot exceed ${GIVEN_MAX} characters`,
  phoneDigitsOnly: PHONE_MSG.digitsOnly,
  phoneMax: PHONE_MSG.max,
  dobFormat: 'Date must be in YYYY/MM/DD format',
  addressInvalid: 'Address contains invalid characters',
  addressMax: `Address cannot exceed ${ADDRESS_MAX} characters`,
  cityInvalid: 'City contains invalid characters',
  cityMax: `City cannot exceed ${CITY_MAX} characters`,
  canadaPostalFormat: 'Postal code must be in A1A 1A1 format',
  usPostalFormat: 'Please enter a valid US zip code (e.g., 12345 or 12345-6789)',
  emailFormat: 'Please enter a valid email address',
  passwordFormat: 'Password must be at least 8 characters and contain at least one uppercase letter and one digit',
} as const

export const EXTENDED_FORM_STEPS = {
  ...FORM_STEPS,
  CONSENT: 9,
  COMPLETION: 10,
  E_SIGNATURE: 8,
}

const PI = FIELD_KEYS.PERSONAL_INFO
const NC = FIELD_KEYS.NAME_CHANGE

export const INITIAL_FORM_DATA = {
  oricode: null,
  personal: {
    country: COUNTRY_CODE.CA,
    [PI.SURNAME]: '',
    [PI.GIVEN_NAME_1]: '',
    [PI.GIVEN_NAME_2]: '',
    [PI.GIVEN_NAME_3]: '',
    [PI.GIVEN_NAME_4]: '',
    genderCode: GENDER.UNKNOWN,
    dateOfBirth: '',
    address: '',
    city: '',
    province: '',
    postalCode: '',
    phoneNumber: '',
    email: '',
  },
  services: {
    services: [],
    reason: '',
    otherReasonDescription: '',
    applicationTypeCode: '',
  },
  vsc: {
    positionDescription: '',
    personOrOrganizationName: '',
    vulnerablePersonDetails: '',
  },
  employmentType: {
    employmentType: '',
    jobTitle: '',
    employer: '',
  },
  nameChange: {
    [NC.NEW_SURNAME]: '',
    [NC.NEW_GIVEN_NAME_1]: '',
    [NC.NEW_GIVEN_NAME_2]: '',
    [NC.NEW_GIVEN_NAME_3]: '',
    [NC.NEW_GIVEN_NAME_4]: '',
  },
  referral: {
    channel: '',
    otherChannelDescription: '',
  },
  photo: {
    images: [],
    slotData: [],
  },
  consent: {
    agreed: false,
  },
  eSignature: {
    data: '',
    pdfImageData: [],
  },
} satisfies FormData

export const ALLOWED_NONCHAR = [
  'Tab',
  'Enter',
  'Escape',
  'Delete',
  'ArrowUp',
  'Backspace',
  'ArrowLeft',
  'ArrowDown',
  'ArrowRight',
] as readonly string[]

export const MODIFIER_KEYS = new Set<React.ModifierKey>([
  'Fn',
  'Alt',
  'Meta',
  'Hyper',
  'Shift',
  'Super',
  'Symbol',
  'FnLock',
  'Control',
  'NumLock',
  'AltGraph',
  'CapsLock',
  'ScrollLock',
  'SymbolLock',
] as const)

export const ALLOWED_CHAR_IN_LOWER = ['a', 'c', 'x', 'v'] as readonly string[]

export const SERVICES = {
  RCMP_CERTIFICATE: 'rcmp_certificate',
  CARD_TO_AUTHORITY: 'card_to_authority',
} as const

export const STAFF_F1_DISPLAY_CHAT_NAME = "F1 Staff"
export const STAFF_VIETCAN_DISPLAY_CHAT_NAME = "VietCan Staff"
export const STAFF_EMAIL = "international@f1fingerprint.ca"


export const NOTES_SAVE_TIMEOUT = 1000